import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { BackIcon, RefreshIcon } from './Icons';

// Funkcja do tasowania tablicy
function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

// Funkcja do generowania błędnych odpowiedzi
function getWrongOptions(allWords, correctAnswer, key) {
  const wrongAnswers = [];
  // Filtrujemy słowa, aby nie zawierały poprawnej odpowiedzi
  const filteredWords = allWords.filter(word => word[key] !== correctAnswer[key]);

  // Wybieramy 2 losowe błędne odpowiedzi
  while (wrongAnswers.length < 2 && filteredWords.length > 0) {
    const randomIndex = Math.floor(Math.random() * filteredWords.length);
    const randomWord = filteredWords.splice(randomIndex, 1)[0]; // Usuwamy, aby uniknąć duplikatów
    if (randomWord) {
      wrongAnswers.push(randomWord[key]);
    }
  }
  return wrongAnswers;
}

export default function MultipleChoiceApp({ words, onBack }) {
  const [shuffledWords, setShuffledWords] = useState(() => shuffle(words));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);
  const timerRef = useRef(null);
  const [isHardMode, setIsHardMode] = useState(false); // Domyślnie tryb normalny (bez czasu)
  const [languageDirection, setLanguageDirection] = useState('pl-de'); // 'pl-de' lub 'de-pl'

  const currentWord = shuffledWords[currentIndex];

  const sourceLang = languageDirection === 'pl-de' ? 'pl' : 'de';
  const targetLang = languageDirection === 'pl-de' ? 'de' : 'pl';

  // Generowanie opcji przy zmianie słowa
  useEffect(() => {
    if (currentWord) {
      const wrongOptions = getWrongOptions(words, currentWord, targetLang);
      const allOptions = shuffle([currentWord[targetLang], ...wrongOptions]);
      setOptions(allOptions);
    }
  }, [currentWord, words, targetLang]);

  const toggleLanguageDirection = () => {
    setLanguageDirection(prev => (prev === 'pl-de' ? 'de-pl' : 'pl-de'));
    setSelectedAnswer(null);
    setIsCorrect(null);
  };
  
  const toggleHardMode = () => {
    setIsHardMode(prev => !prev);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  // Efekt dla licznika czasu
  useEffect(() => {
    // Jeśli nie jest w trybie 'hard', upewnij się, że timer jest wyczyszczony
    if (!isHardMode) {
      clearInterval(timerRef.current);
      return;
    }

    // Jeśli wybrano odpowiedź lub test jest ukończony, zatrzymaj timer
    if (selectedAnswer || completed) {
      clearInterval(timerRef.current);
      return;
    }

    // W trybie 'hard', resetuj czas i uruchom nowy licznik
    setTimeLeft(5);
    timerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          handleTimeout(); // Czas się skończył
        }
        return prevTime > 1 ? prevTime - 1 : 0;
      });
    }, 1000);

    // Funkcja czyszcząca
    return () => clearInterval(timerRef.current);
  }, [currentIndex, selectedAnswer, completed, isHardMode]);

  const handleAnswerClick = (answer) => {
    if (selectedAnswer) return; // Zapobiegaj wielokrotnemu klikaniu
    clearInterval(timerRef.current); // Zatrzymaj licznik

    setSelectedAnswer(answer);
    if (answer === currentWord[targetLang]) {
      setIsCorrect(true);
      setTimeout(() => {
        handleNext();
      }, 800); // Automatyczne przejście po 0.8s
    } else {
      setIsCorrect(false);
      setScore(s => s + 1);
    }
  };

  const handleTimeout = () => {
    if (selectedAnswer) return;
    setSelectedAnswer('timeout'); // Specjalny stan dla braku odpowiedzi
    setIsCorrect(false);
    setScore(s => s + 1);
  };

  const handleNext = () => {
    if (currentIndex + 1 < shuffledWords.length) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsCorrect(null);
      // Timer zostanie zresetowany przez useEffect
    } else {
      setCompleted(true);
    }
  };

  const restart = () => {
    setShuffledWords(shuffle(words)); // Ponowne tasowanie słów przy restarcie
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setScore(0);
    setCompleted(false);
    // isHardMode i languageDirection pozostają bez zmian
    // Timer zostanie zresetowany przez useEffect
  };

  if (completed) {
    return (
      <>
        <div className="flashcard-container">
          <h1>🎉 Gratulacje!</h1>
          <p>Ukończono test wyboru.</p>
          <p>Punkty karne: <strong>{score}</strong></p>
          <button onClick={restart}><RefreshIcon /> Spróbuj jeszcze raz</button>
        </div>
        <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
      </>
    );
  }

  return (
    <>
      <div className="flashcard-container">
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${Math.round((currentIndex + 1) / words.length * 100)}%` }}></div>
        </div>
        {isHardMode && (
          <div className={`timer ${timeLeft <= 1 ? 'danger' : ''}`}>
            {timeLeft}
          </div>
        )}
        <h1>📝 Test wyboru</h1>
        <div className="language-toggle-container">
          <button onClick={toggleLanguageDirection} className="language-toggle-button">
            {languageDirection === 'pl-de' ? 'PL ➔ DE' : 'DE ➔ PL'}
          </button>
        </div>
        <button onClick={toggleHardMode} className="hard-mode-toggle">
          {isHardMode ? '🔥 Hard' : '😎 Normal'}
        </button>
        <p style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
          Słowo: <span style={{ fontWeight: 'normal' }}>{currentWord[sourceLang]}</span>
        </p>
        {selectedAnswer === 'timeout' && (
          <p style={{ color: '#f5576c', fontWeight: 'bold' }}>Czas minął!</p>
        )}
        <div className="multiple-choice-options">
          {options.map((option, i) => (
            <button key={i} onClick={() => handleAnswerClick(option)} className={`choice-button ${selectedAnswer && (option === currentWord[targetLang] ? 'correct' : (option === selectedAnswer ? 'incorrect' : ''))}`} disabled={!!selectedAnswer}>
              {option}
            </button>
          ))}
        </div>
        {selectedAnswer && !isCorrect && (
          <button onClick={handleNext} style={{ marginTop: '1.5rem' }}>Następne</button>
        )}
      </div>
      <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
    </>
  );
}